import dayjs from 'dayjs';

import { useCallback, useState } from 'react';

import {
  deleteShipmentDelta,
  deleteVoyageDelta,
  upsertVoyageDelta,
} from '../../../shared/api/scenarioApi';

import { StorageKey } from '../../../shared/auth/DataService';

import { useScenario } from '../../../shared/contexts/ScenarioContext';

import useScenarioMode from '../../../shared/hooks/useScenarioMode';

import { addVoyages, updateVoyageGroup } from '../services/VoyageRepository';

import { useReportingCoordination } from '../store';

import type { VesselFormValues } from '../views/schedule/components/RHFVesselForm';

import {
  formatDateOnly,
  isDestDirty,
} from '../views/sales/modals/groupEditHelpers';

import type { ShipGroup } from '../views/sales/modals/groupEditTypes';

interface UseGroupEditSubmitProps {
  isCreateMode: boolean;
  freshGroup: ShipGroup | null;
  editData: Partial<VesselFormValues> | undefined;
  deltaJsonMap: Record<string, Record<string, unknown>>;
  voyageChangeTypeMap: Record<string, string>;
  voyageDeltaIdByVoyageId: Record<string, string>;
  createdVoyageDeltas: Array<{
    id: string;
    deltaJson: Record<string, unknown>;
  }>;
  groupShipmentDeltaIds: string[];
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info'
  ) => void;
  onClose: () => void;
}

interface UseGroupEditSubmitResult {
  handleSubmit: (formData: VesselFormValues) => Promise<void>;
  handleUndoGroupDeltas: () => Promise<void>;
  isSubmitting: boolean;
  isUndoing: boolean;
}

const useGroupEditSubmit = ({
  isCreateMode,
  freshGroup,
  editData,
  deltaJsonMap,
  voyageChangeTypeMap,
  voyageDeltaIdByVoyageId,
  createdVoyageDeltas,
  groupShipmentDeltaIds,
  showSnackbar,
  onClose,
}: UseGroupEditSubmitProps): UseGroupEditSubmitResult => {
  const { isScenarioActive } = useScenarioMode();
  const { activeScenario, resetEditingTimer } = useScenario();
  const { fetchAllData } = useReportingCoordination();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);

  // ── Create handler ──────────────────────────────────────────

  const handleCreate = useCallback(
    async (formData: VesselFormValues) => {
      const scenarioId = activeScenario?.id;

      if (isScenarioActive && scenarioId) {
        // Scenario path: one upsertVoyageDelta per destination
        const calls = formData.destinations.map((dest) =>
          upsertVoyageDelta(scenarioId, {
            changeType: 'CREATED',
            patch: {
              ship: formData.imo ? { imo: formData.imo } : null,
              voyageNumber: formData.voyageNumber || 'VOY' + Date.now(),
              carrier: formData.carrier
                ? { supplierNo: formData.carrier }
                : null,
              departureCountry: formData.departureCountry
                ? { countryCode: formData.departureCountry }
                : null,
              departurePort: formData.departurePort
                ? { unLocode: formData.departurePort }
                : null,
              portOperator: formData.portOperator
                ? { supplierNo: formData.portOperator }
                : null,
              slotConfirmed: formData.slotConfirmed ?? false,
              actualETD: formData.actualETD
                ? dayjs(formData.actualETD).format('YYYY-MM-DDTHH:mm:ss')
                : null,
              destinationCountry: dest.destinationCountry
                ? { countryCode: dest.destinationCountry }
                : null,
              destinationPort: dest.destinationPort
                ? { unLocode: dest.destinationPort }
                : null,
              actualETA: dest.actualETA
                ? dayjs(dest.actualETA).format('YYYY-MM-DDTHH:mm:ss')
                : null,
              generalRemarks: dest.generalRemarks || '',
              shipments: dest.shipments.map((v) => ({
                market: v.market != null ? { id: v.market } : null,
                firstShippingLineOffer: v.firstShippingLineOffer,
                adjustedPORequest: v.adjustedPORequest,
                finalAgreement: v.finalAgreement,
                effectiveShipped: v.effectiveShipped,
              })),
            },
          })
        );

        try {
          await Promise.all(calls);
          resetEditingTimer();
          const count = formData.destinations.length;
          showSnackbar(
            count === 1
              ? 'Voyage scenario change saved'
              : `${count} voyage scenario changes saved`,
            'success'
          );
          fetchAllData();
          onClose();
        } catch {
          showSnackbar('Error saving voyage scenario change(s)');
        }
        return;
      }

      // Normal path: addVoyages
      const payload = {
        imo: formData.imo,
        voyageNumber: formData.voyageNumber || 'VOY' + Date.now(),
        carrier: formData.carrier ?? null,
        shippingCompany: formData.shippingCompany ?? null,
        departurePort: formData.departurePort,
        portOperator: formData.portOperator ?? null,
        slotConfirmed: formData.slotConfirmed ?? false,
        initialETD: formData.initialETD || null,
        actualETD: formData.actualETD || null,
        destinations: formData.destinations.map((dest) => ({
          destinationPort: dest.destinationPort,
          initialETA: dest.initialETA || null,
          actualETA: dest.actualETA || null,
          generalRemarks: dest.generalRemarks || '',
          shipments: dest.shipments.map((v) => ({
            market: v.market,
            firstShippingLineOffer: v.firstShippingLineOffer,
            adjustedPORequest: v.adjustedPORequest,
            finalAgreement: v.finalAgreement,
            effectiveShipped: v.effectiveShipped,
          })),
        })),
      };

      try {
        await addVoyages(payload);
        const count = formData.destinations.length;
        showSnackbar(
          count === 1
            ? 'Added Voyage successfully'
            : `Added ${count} Voyages successfully`,
          'success'
        );
        fetchAllData();
        onClose();
      } catch {
        showSnackbar('Error adding voyage(s)');
      }
    },
    [
      isScenarioActive,
      activeScenario,
      resetEditingTimer,
      fetchAllData,
      onClose,
      showSnackbar,
    ]
  );

  // ── Group-edit handler ──────────────────────────────────────

  const handleGroupEdit = useCallback(
    async (formData: VesselFormValues) => {
      const groupKey = freshGroup?.key;
      if (!groupKey) return;

      const scenarioId = activeScenario?.id;

      if (isScenarioActive && scenarioId) {
        // Scenario path: upsert/cancel deltas per destination
        const calls: Promise<unknown>[] = [];
        const baselineDests: any[] = editData?.destinations ?? [];
        const baselineTop = editData as any;

        const isTopLevelDirty =
          (formData.imo ?? '') !== (baselineTop?.imo ?? '') ||
          formatDateOnly(formData.actualETD) !==
            formatDateOnly(baselineTop?.actualETD) ||
          (formData.departureCountry ?? '') !==
            (baselineTop?.departureCountry ?? '') ||
          (formData.departurePort ?? '') !==
            (baselineTop?.departurePort ?? '') ||
          String(formData.carrier ?? '') !==
            String(baselineTop?.carrier ?? '') ||
          String(formData.portOperator ?? '') !==
            String(baselineTop?.portOperator ?? '') ||
          (formData.slotConfirmed ?? false) !==
            (baselineTop?.slotConfirmed ?? false) ||
          (formData.voyageNumber ?? '') !== (baselineTop?.voyageNumber ?? '');

        // Helper: build a full patch.
        // The backend now handles saving only changed fields for MODIFIED
        // voyages/shipments, so the frontend always sends the full patch.
        const buildFullPatch = (dest: any) => ({
          ship: formData.imo ? { imo: formData.imo } : null,
          carrier: formData.carrier ? { supplierNo: formData.carrier } : null,
          voyageNumber: formData.voyageNumber ?? '',
          portOperator: formData.portOperator
            ? { supplierNo: formData.portOperator }
            : null,
          departureCountry: formData.departureCountry
            ? { countryCode: formData.departureCountry }
            : null,
          departurePort: formData.departurePort
            ? { unLocode: formData.departurePort }
            : null,
          slotConfirmed: formData.slotConfirmed ?? false,
          actualETD: formData.actualETD
            ? dayjs(formData.actualETD).format('YYYY-MM-DDTHH:mm:ss')
            : null,
          destinationCountry: dest.destinationCountry
            ? { countryCode: dest.destinationCountry }
            : null,
          destinationPort: dest.destinationPort
            ? { unLocode: dest.destinationPort }
            : null,
          actualETA: dest.actualETA
            ? dayjs(dest.actualETA).format('YYYY-MM-DDTHH:mm:ss')
            : null,
          generalRemarks: dest.generalRemarks ?? '',
          shipments: dest.shipments.map((s: any) => ({
            id: s.id || undefined,
            market: s.market != null ? { id: s.market } : null,
            firstShippingLineOffer: s.firstShippingLineOffer ?? 0,
            adjustedPORequest: s.adjustedPORequest ?? 0,
            finalAgreement: s.finalAgreement ?? 0,
            effectiveShipped: s.effectiveShipped ?? 0,
          })),
        });

        formData.destinations.forEach((dest: any, idx: number) => {
          const voyageId: string | undefined = dest._voyageId ?? undefined;
          const isScenarioCreated: boolean = dest._scenarioCreated === true;
          const baseline = baselineDests[idx];

          const fullPatch = buildFullPatch(dest);

          const createdPatch = {
            ...fullPatch,
            voyageNumber: formData.voyageNumber || 'VOY' + Date.now(),
          };

          if (dest._cancelled && voyageId && !isScenarioCreated) {
            // Skip if already cancelled in DB (baseline) or already persisted as CANCELLED delta
            const wasAlreadyCancelled = baseline?._cancelled === true;
            if (
              !wasAlreadyCancelled &&
              voyageChangeTypeMap[voyageId] !== 'CANCELLED'
            ) {
              calls.push(
                upsertVoyageDelta(scenarioId, {
                  voyageId,
                  changeType: 'CANCELLED',
                  patch: { ...fullPatch, cancelled: true },
                })
              );
            }
          } else if (dest._cancelled && isScenarioCreated) {
            // Cancelling a voyage that was created within this scenario:
            // send CANCELLED so the backend physically deletes the CREATED delta.
            const deltaId = dest._deltaId ?? voyageId;
            if (deltaId) {
              calls.push(
                upsertVoyageDelta(scenarioId, {
                  voyageId: deltaId,
                  changeType: 'CANCELLED',
                  patch: { ...fullPatch, cancelled: true },
                })
              );
            }
          } else if (!dest._cancelled && isScenarioCreated) {
            // Scenario-created destination: always re-save via CREATED path.
            // The backend's handleModifyCreatedVoyage deduplicates shipments by
            // market id, so re-sending is always safe and necessary to persist changes.
            // deltaId = dest._deltaId (pure new) or dest._voyageId (collision case
            // where the backend used the real voyage UUID as the delta's own id).
            const deltaId = dest._deltaId ?? voyageId;
            calls.push(
              upsertVoyageDelta(scenarioId, {
                ...(deltaId && { voyageId: deltaId }),
                changeType: 'CREATED',
                patch: createdPatch,
              })
            );
          } else if (!dest._cancelled && voyageId && !isScenarioCreated) {
            // Existing voyage — only submit if something actually changed
            const cancelledInBackend =
              voyageChangeTypeMap[voyageId] === 'CANCELLED';
            const restoredFromCancelled =
              baseline?._cancelled === true && !dest._cancelled;
            if (
              cancelledInBackend ||
              restoredFromCancelled ||
              isTopLevelDirty ||
              isDestDirty(dest, baseline)
            ) {
              // Strip unchanged date fields: the DatePicker loses the time
              // component, causing the backend to falsely detect a change.
              // However, if the field already carries a pre-existing scenario
              // delta, we must keep it in the patch so the backend preserves
              // that prior change (otherwise it would be silently dropped).
              const modifiedPatch: Record<string, unknown> = { ...fullPatch };
              const existingDelta = voyageId
                ? deltaJsonMap[voyageId]
                : undefined;
              const etaHasExistingDelta =
                existingDelta &&
                ('actualETA' in existingDelta || 'initialETA' in existingDelta);
              const etdHasExistingDelta =
                existingDelta &&
                ('actualETD' in existingDelta || 'initialETD' in existingDelta);

              if (
                !etaHasExistingDelta &&
                baseline &&
                formatDateOnly(dest.actualETA) ===
                  formatDateOnly(baseline.actualETA)
              ) {
                delete modifiedPatch.actualETA;
              }
              if (
                !etdHasExistingDelta &&
                formatDateOnly(formData.actualETD) ===
                  formatDateOnly(baselineTop?.actualETD)
              ) {
                delete modifiedPatch.actualETD;
              }
              // When restoring a DB-cancelled voyage in scenario mode,
              // include cancelled: false so the overlay un-cancels it.
              if (restoredFromCancelled) {
                modifiedPatch.cancelled = false;
              }
              calls.push(
                upsertVoyageDelta(scenarioId, {
                  voyageId,
                  changeType: 'MODIFIED',
                  patch: modifiedPatch,
                })
              );
            }
          } else if (!dest._cancelled && !voyageId && !isScenarioCreated) {
            // Brand-new destination added this session (no prior delta)
            calls.push(
              upsertVoyageDelta(scenarioId, {
                changeType: 'CREATED',
                patch: createdPatch,
              })
            );
          }
        });

        try {
          await Promise.all(calls);
          resetEditingTimer();
          showSnackbar('Scenario voyage changes saved', 'success');
          fetchAllData();
          onClose();
        } catch (error: any) {
          // eslint-disable-next-line no-console
          console.error('[GroupEditModal] Scenario update failed:', error);
          showSnackbar(
            error?.message || 'Error saving scenario voyage changes'
          );
        }
        return;
      }

      // Normal path: updateVoyageGroup
      const emea = localStorage.getItem(StorageKey.userInfo_sub);

      const activeDestinations = formData.destinations
        .filter((dest: any) => !dest._cancelled)
        .map((dest: any) => ({
          voyageId: dest._voyageId ?? undefined,
          destinationCountry: dest.destinationCountry,
          destinationPort: dest.destinationPort,
          initialETA: dest.initialETA || null,
          actualETA: dest.actualETA || null,
          generalRemarks: dest.generalRemarks ?? '',
          shipments: dest.shipments.map((s: any) => ({
            id: s.id || undefined,
            market: s.market,
            firstShippingLineOffer: s.firstShippingLineOffer ?? 0,
            adjustedPORequest: s.adjustedPORequest ?? 0,
            finalAgreement: s.finalAgreement ?? 0,
            effectiveShipped: s.effectiveShipped ?? 0,
          })),
        }));

      const cancelledVoyageIds = formData.destinations
        .filter((dest: any) => dest._cancelled && dest._voyageId)
        .map((dest: any) => dest._voyageId);

      const payload = {
        groupKey,
        imo: formData.imo,
        carrier: formData.carrier ?? null,
        shippingCompany: formData.shippingCompany ?? null,
        departurePort: formData.departurePort,
        departureCountry: formData.departureCountry,
        portOperator: formData.portOperator ?? null,
        actualETD: formData.actualETD
          ? dayjs(formData.actualETD).format('YYYY-MM-DDTHH:mm:ss')
          : null,
        slotConfirmed: formData.slotConfirmed ?? false,
        voyageNumber: formData.voyageNumber ?? '',
        lastUpdatedBy: { emea },
        destinations: activeDestinations,
        cancelledVoyageIds,
      };

      try {
        await updateVoyageGroup(payload);
        showSnackbar('Voyage group updated successfully', 'success');
        fetchAllData();
        onClose();
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('[GroupEditModal] Update failed:', error);
        showSnackbar(error?.message || 'Error updating voyage group');
      }
    },
    [
      freshGroup,
      isScenarioActive,
      activeScenario,
      resetEditingTimer,
      editData,
      deltaJsonMap,
      voyageChangeTypeMap,
      fetchAllData,
      onClose,
      showSnackbar,
    ]
  );

  // ── Undo handler ────────────────────────────────────────────

  const handleUndoGroupDeltas = useCallback(async () => {
    const scenarioId = activeScenario?.id;
    if (!scenarioId || isUndoing) return;

    setIsUndoing(true);
    try {
      // Identify orphaned shipment deltas (those without a voyage delta parent)
      const voyageDeltaVoyageIds = new Set(
        Object.keys(voyageDeltaIdByVoyageId)
      );
      const createdDeltaIds = new Set(createdVoyageDeltas.map((d) => d.id));
      const orphanedShipmentDeltaIds = groupShipmentDeltaIds.filter(
        (id) => !voyageDeltaVoyageIds.has(id) && !createdDeltaIds.has(id)
      );

      // Delete orphaned shipment deltas first
      if (orphanedShipmentDeltaIds.length > 0) {
        await Promise.all(
          orphanedShipmentDeltaIds.map((id) =>
            deleteShipmentDelta(scenarioId, id)
          )
        );
      }

      // Then delete voyage deltas (cascades to non-orphaned shipment deltas)
      const calls: Promise<unknown>[] = [
        ...Object.values(voyageDeltaIdByVoyageId).map((deltaId) =>
          deleteVoyageDelta(scenarioId, deltaId)
        ),
        ...createdVoyageDeltas.map((cvd) =>
          deleteVoyageDelta(scenarioId, cvd.id)
        ),
      ];
      await Promise.all(calls);
      resetEditingTimer();
      showSnackbar('Scenario changes for this voyage group undone', 'success');
      fetchAllData();
      onClose();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('[GroupEditModal] Undo failed:', error);
      showSnackbar(error?.message || 'Error undoing scenario changes');
    } finally {
      setIsUndoing(false);
    }
  }, [
    activeScenario,
    resetEditingTimer,
    isUndoing,
    voyageDeltaIdByVoyageId,
    createdVoyageDeltas,
    groupShipmentDeltaIds,
    showSnackbar,
    fetchAllData,
    onClose,
  ]);

  // ── Top-level dispatcher ────────────────────────────────────

  const handleSubmit = useCallback(
    async (formData: VesselFormValues) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        if (isCreateMode) {
          await handleCreate(formData);
        } else {
          await handleGroupEdit(formData);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, isCreateMode, handleCreate, handleGroupEdit]
  );

  return { handleSubmit, handleUndoGroupDeltas, isSubmitting, isUndoing };
};

export default useGroupEditSubmit;
